app.service("PatientFormsAPI", [
	"$http",
	"ENV",
	"Session",
	function ($http, ENV, Session) {
		function formatDob(dob) {
			if (!dob) return "";

			if (typeof dob === "string") {
				var isoMatch = dob.match(/^(\d{4}-\d{2}-\d{2})/);
				if (isoMatch) return isoMatch[1];
			}

			var date = dob instanceof Date ? dob : new Date(dob);
			if (Number.isNaN(date.getTime())) return "";

			var month = String(date.getMonth() + 1).padStart(2, "0");
			var day = String(date.getDate()).padStart(2, "0");
			return date.getFullYear() + "-" + month + "-" + day;
		}

		var service = {
			isProcessing: false,
			createCustomForm: function (payload) {
				return $http({
					method: "POST",
					url: ENV.API + "/api/patient-forms/custom-forms",
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${Session.practice.id}`,
					},
					data: payload,
				});
			},
			updateCustomForm: function (templateId, payload) {
				return $http({
					method: "PUT",
					url: ENV.API + `/api/patient-forms/custom-forms/${templateId}`,
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${Session.practice.id}`,
					},
					data: payload,
				});
			},
			deleteCustomForm: function (templateId) {
				return $http({
					method: "DELETE",
					url: ENV.API + `/api/patient-forms/custom-forms/${templateId}`,
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${Session.practice.id}`,
					},
				});
			},
			getCustomForms: function () {
				return $http({
					method: "GET",
					url: ENV.API + "/api/patient-forms/custom-forms",
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${Session.practice.id}`,
					},
				});
			},
			sendCustomFormTemplateToPatient: function (templateId, patientId) {
				return $http({
					method: "POST",
					url: ENV.API + `/api/patient-forms/custom-forms/${templateId}/patient/${patientId}/send`,
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${Session.practice.id}`,
					},
				});
			},
			/* Image bytes are a second phase: the template is saved first so the
			   component ids exist, then each image is PUT against its own option.
			   Content-Type is deliberately undefined so the browser writes the
			   multipart boundary itself, and identity transform stops Angular
			   serialising the FormData to JSON. */
			uploadComponentImage: function (templateId, componentId, file, altText) {
				var formData = new FormData();
				formData.append("file", file);
				if (altText) {
					formData.append("altText", altText);
				}

				return $http({
					method: "PUT",
					url: ENV.API + `/api/patient-forms/custom-forms/${templateId}/components/${componentId}/image`,
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${Session.practice.id}`,
						"Content-Type": undefined,
					},
					transformRequest: angular.identity,
					data: formData,
				});
			},
			/* Staff-side image fetch. An <img src> cannot carry the bearer token, so
			   the bytes come back as a blob for the caller to turn into an object URL. */
			getComponentImage: function (templateId, componentId) {
				return $http({
					method: "GET",
					url: ENV.API + `/api/patient-forms/custom-forms/${templateId}/components/${componentId}/image`,
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${Session.practice.id}`,
					},
					responseType: "blob",
				});
			},
			/* Patient-side. Token-scoped and unauthenticated, so this one can be an
			   <img src> directly. */
			getPatientComponentImageUrl: function (token, componentId) {
				if (!token || componentId === null || componentId === undefined) return "";
				return ENV.API + `/api/patient-forms/custom-forms/token/${token}/components/${componentId}/image`;
			},
			getCustomFormAnswers: function (templateId, patientId) {
				return $http({
					method: "GET",
					url: ENV.API + `/api/patient-forms/custom-forms/${templateId}/patient/${patientId}/answer`,
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${Session.practice.id}`,
					},
				});
			},
			getAllCustomFormAnswersForTemplate: function (templateId) {
				return $http({
					method: "GET",
					url: ENV.API + `/api/patient-forms/custom-forms/${templateId}/answers`,
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${Session.practice.id}`,
					},
				});
			},
			/* Kicks off a server-side export of every answer for the template. The
			   render runs in a Lambda after this returns and publishes
			   CUSTOM_FORM_EXPORT_READY with the download URL once the file lands. */
			exportCustomFormAnswers: function (templateId) {
				return $http({
					method: "POST",
					url: ENV.API + `/api/patient-forms/custom-forms/${templateId}/answers/export`,
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${Session.practice.id}`,
					},
				});
			},
			verifyToken: function (tokenTypeOrToken, tokenOrDob, maybeDob) {
				var tokenType = "PATIENT_CUSTOM_FORM";
				var token = tokenTypeOrToken;
				var dob = tokenOrDob;

				var looksLikeTokenType = typeof tokenTypeOrToken === "string"
					&& tokenTypeOrToken === tokenTypeOrToken.toUpperCase()
					&& tokenTypeOrToken.indexOf("_") > -1;

				if (looksLikeTokenType) {
					tokenType = tokenTypeOrToken;
					token = tokenOrDob;
					dob = maybeDob;
				}

				var url = ENV.API + `/api/patient-tokens/${tokenType}/token/${token}`;
				if (dob) {
					url += `?dob=${encodeURIComponent(dob)}`;
				}

				return $http({
					method: "GET",
					url: url,
					// url: ENV.API + `/api/patient-tokens/${encodeURIComponent(tokenType)}/token/${encodeURIComponent(token)}`,
				});
			},
			submitLensChoice: function (token, dob, selectionOptionIdOd, selectionOptionIdOs, patientComments) {
				return $http({
					method: "POST",
					url: ENV.API + `/api/patient-tokens/PREMIUM_LENS_SELECTION_CHOICE/token/${encodeURIComponent(token)}?dob=${encodeURIComponent(dob)}`,
					data: {
						"selection_option_id_od": selectionOptionIdOd,
						"selection_option_id_os": selectionOptionIdOs,
						"patient_comments": patientComments
					}
				});
			},
			submitPatientCustomFormByToken: function (token, dob, payload) {
				var formattedDob = formatDob(dob);
				return $http({
					method: "POST",
					url: ENV.API + `/api/patient-tokens/PATIENT_CUSTOM_FORM/token/${token}?dob=${encodeURIComponent(formattedDob)}`,
					data: payload
				});
			},
			// Texts the token's existing code to the patient's mobile again. Sits outside
			// /api/patient-tokens because it is not a token validation or completion, but it is
			// unauthenticated in the same way and for the same reason: the page that calls it is the
			// one the patient reached from a link, and the token in the path is the authorisation.
			//
			// Only worth calling when the validated token reported mobile_code_resend_available.
			// Every refusal comes back as a bare 400 by design, so the caller cannot tell which of
			// them it was and should say something that covers all of them.
			resendMobileCode: function (token) {
				return $http({
					method: "POST",
					url: ENV.API + `/api/patient-verification/resend-code/${encodeURIComponent(token)}`,
				});
			},
			// Proves ONE factor - the texted code or the date of birth - at the step of the
			// verification page that asks for it, so a wrong answer is caught where it was typed
			// rather than after the consent questions. Exactly one of the two is sent per call.
			//
			// Sits outside /api/patient-tokens for the same reason resendMobileCode does: it is
			// neither a token validation nor a completion. Unauthenticated in the same way and for
			// the same reason - the page that calls it was reached from a link, and the token in the
			// path is the authorisation.
			//
			// The date of birth goes in the body rather than the query string, unlike the older
			// patient form tokens: a date of birth in a URL is written to access logs.
			//
			// Both success and rejection carry attempts_remaining, so the page can warn the patient
			// before the link closes on them, and a reason - DOB_MISMATCH, EXPIRED, ERROR and so on -
			// so it can say what actually went wrong.
			verifyStep: function (token, mobileCode, dob) {
				return $http({
					method: "POST",
					url: ENV.API + `/api/patient-verification/verify-step/${encodeURIComponent(token)}`,
					data: {
						"mobile_code": mobileCode,
						"dob": dob
					}
				});
			},
			// Completes the verification. Carries the consents only: the code and the date of birth
			// were each proven by verifyStep() above and recorded against the token, so there is
			// nothing left here that can be wrong.
			submitContactVerification: function (token, research, ml) {
				return $http({
					method: "POST",
					url: ENV.API + `/api/patient-tokens/PATIENT_COMM_VERIFICATION/token/${encodeURIComponent(token)}`,
					data: {
						"consent_data_research": research,
						"consent_data_ml": ml
					}
				});
			},
		};

		return service;
	},
]);